import cuid from "cuid";
import {
  PLAYER_PROJECTILE_SPEED,
  ENEMY_INITIAL_HEALTH,
  ENEMY_SPEED,
  GRAVITY,
  GROUND_LEVEL,
  GROUND_WIDTH,
  PLAYER_HEIGHT,
  PLAYER_ID,
  PLAYER_JUMP_SPEED,
  PLAYER_INITIAL_HEALTH,
  PLAYER_MOVEMENT_SPEED,
  PLAYER_WIDTH,
  ENEMY_RADIUS,
  ENEMY_FIRING_INTERVAL,
  ENEMY_PROJECTILE_SPEED,
  PROJECTILE_RADIUS,
  WRECK_FALL_DELAY,
  GRID_COLUMNS,
  GRID_ROWS,
  GRID_LEFT,
  GRID_RIGHT,
  GRID_TOP,
  GRID_BOTTOM,
  PRECISION,
  HEALTH_POWER_UP_AMOUNT,
  HEALTH_POWER_UP_RADIUS,
  POWER_UP_SPAWN_START_Y,
} from "./constants";
import {
  Box,
  distance,
  identityVector,
  Point,
  rayBoxIntersection,
  RayIntersection,
  vectorAdd,
  vectorLength,
  vectorMul,
  vectorSub,
} from "./math";

export type Player = {
  id: string;
  type: "player";
  position: Point;
  moveIntent: number;
  velocityY: number;
  health: number;
};

type Projectile = {
  id: string;
  type: "projectile";
  position: Point;
  velocity: Point;
  ownerId: string;
};

type Enemy = {
  id: string;
  type: "enemy";
  subType: "normal" | "exploding";
  position: Point;
  direction: Point;
  health: number;
  timeSinceLastFired: number;
  lifetime: number;
};

type BaseWreck = {
  id: string;
  type: "wreck";
  position: Point;
  velocityY: number;
  lifetime: number; // in seconds
};

type UnassignedWreck = BaseWreck & {
  slot: null;
};

type AssignedWreck = BaseWreck & {
  slot: { row: number; column: number };
};

type Wreck = UnassignedWreck | AssignedWreck;

type HealthPowerUp = {
  id: string;
  type: "health-power-up";
  position: Point;
};

type Entity = Player | Projectile | Enemy | Wreck | HealthPowerUp;

export type State = {
  time: number;
  score: number;
  lastPowerUpSpawnY: number;
  enemySpawnedAt: number;
  entities: Entity[];
};

type TickAction = {
  type: "TICK";
  timeDelta: number;
};

type MoveAction = {
  type: "MOVE";
  playerId: string;
  moveIntent: number;
};

type JumpAction = {
  type: "JUMP";
  playerId: string;
};

type ShootAction = {
  type: "SHOOT";
  playerId: string;
  direction: Point;
};

type SpawnEnemyAction = {
  type: "SPAWN_ENEMY";
};

type RestartAction = {
  type: "RESTART";
};

type DoDamageAction = {
  type: "DO_DAMAGE";
  targetId: string;
  amount: number;
};

type DieAction = {
  type: "DIE";
  targetId: string;
};

type RemoveProjectileAction = {
  type: "REMOVE_PROJECTILE";
  projectileId: string;
};

type SpawnWreckAction = {
  id: string;
  type: "SPAWN_WRECK";
  position: Point;
  shotPosition: Point | null;
};

type ReplenishHealthAction = {
  type: "REPLENISH_HEALTH";
  amount: number;
  targetId: string;
};

type SpawnPowerUpAction = {
  type: "SPAWN_POWER_UP";
  id: string;
  position: Point;
};

type IncreaseScoreAction = {
  type: "INCREASE_SCORE";
  amount: number;
};

export type Action =
  | TickAction
  | MoveAction
  | JumpAction
  | ShootAction
  | SpawnEnemyAction
  | RestartAction
  | DoDamageAction
  | DieAction
  | RemoveProjectileAction
  | SpawnWreckAction
  | ReplenishHealthAction
  | SpawnPowerUpAction
  | IncreaseScoreAction;

export const INITIAL_STATE: State = {
  time: 0,
  score: 0,
  lastPowerUpSpawnY: POWER_UP_SPAWN_START_Y,
  enemySpawnedAt: 0,
  entities: [
    {
      id: PLAYER_ID,
      type: "player",
      position: { x: 0, y: GROUND_LEVEL + PLAYER_HEIGHT / 2 },
      moveIntent: 0,
      velocityY: 0,
      health: PLAYER_INITIAL_HEALTH,
    },
    {
      id: cuid(),
      type: "health-power-up",
      position: {
        x: (GRID_RIGHT - GRID_LEFT) * (Math.random() - 0.5),
        y: POWER_UP_SPAWN_START_Y,
      },
    },
  ],
};

const GRID_WIDTH = GRID_RIGHT - GRID_LEFT;
const GRID_HEIGHT = GRID_TOP - GRID_BOTTOM;
const SLOT_WIDTH = GRID_WIDTH / GRID_COLUMNS;
const SLOT_HEIGHT = GRID_HEIGHT / GRID_ROWS;

const applyToEntityById = (
  applyFn: (entity: Entity) => Entity,
  entityId: string,
  entities: Entity[]
): Entity[] => {
  return entities.map((entity) =>
    entity.id === entityId ? applyFn(entity) : entity
  );
};

export const isHealthPowerUp = (entity: Entity): entity is HealthPowerUp =>
  entity.type === "health-power-up";

const calculateSlotPosition = (column: number, row: number) => {
  return {
    x: GRID_LEFT + (column / GRID_COLUMNS) * GRID_WIDTH + SLOT_WIDTH / 2,
    y: GRID_BOTTOM + (row / GRID_ROWS) * GRID_HEIGHT + SLOT_HEIGHT / 2,
  };
};

const slotToBox = (row: number, column: number): Box => {
  const position = calculateSlotPosition(column, row);
  return {
    top: position.y + SLOT_HEIGHT / 2,
    bottom: position.y - SLOT_HEIGHT / 2,
    left: position.x - SLOT_WIDTH / 2,
    right: position.x + SLOT_WIDTH / 2,
  };
};

export const getSlot = (position: Point) => {
  const column = Math.floor(
    ((position.x - GRID_LEFT) / (GRID_RIGHT - GRID_LEFT)) * GRID_COLUMNS
  );
  if (column < 0 || column >= GRID_COLUMNS) {
    return null;
  }
  const wreckAreaHeight = GRID_TOP - GRID_BOTTOM;
  const row = Math.floor(
    ((position.y - GRID_BOTTOM) / wreckAreaHeight) * GRID_ROWS
  );
  if (row < 0) {
    return null;
  }
  return { row, column };
};

const slotToWreckMapCache = new WeakMap<State, Map<string, Wreck>>();
const generateSlotToWreckMap = (state: State): Map<string, Wreck> => {
  const slotToWreckMap = new Map<string, Wreck>();
  state.entities.filter(isWreck).forEach((wreck) => {
    if (!wreck.slot) {
      return;
    }
    slotToWreckMap.set(`${wreck.slot.row}-${wreck.slot.column}`, wreck);
  });
  return slotToWreckMap;
};

export const getWreckInSlot = (
  row: number,
  column: number,
  state: State
): Wreck | null => {
  const slotToWreckMap =
    slotToWreckMapCache.get(state) || generateSlotToWreckMap(state);
  const wreck = slotToWreckMap.get(`${row}-${column}`);
  if (wreck === undefined) {
    return null;
  }
  const slotPosition = calculateSlotPosition(column, row);
  if (distance(wreck.position, slotPosition) > PRECISION) {
    return null;
  }
  return wreck;
};

const isSlotReservedOrOccupied = (
  row: number,
  column: number,
  state: State
): boolean => {
  const slotToWreckMap =
    slotToWreckMapCache.get(state) || generateSlotToWreckMap(state);
  return slotToWreckMap.get(`${row}-${column}`) !== undefined;
};

const findOccupiedSlotBelow = (
  position: Point,
  state: State
): { row: number; column: number } | null => {
  const startSlot = getSlot({ x: position.x, y: position.y + PRECISION });
  if (!startSlot) {
    return null;
  }
  for (let row = startSlot.row - 1; row >= 0; row--) {
    const wreckId = getWreckInSlot(row, startSlot.column, state);
    if (wreckId) {
      return { row, column: startSlot.column };
    }
  }
  return null;
};

const playerDistanceToNearestPlatform = (
  player: Player,
  state: State
): number => {
  const playerRightEdge = player.position.x + PLAYER_WIDTH / 2;
  const playerLeftEdge = player.position.x - PLAYER_WIDTH / 2;
  const playerBottomEdge = player.position.y - PLAYER_HEIGHT / 2;
  const distanceToGround =
    playerRightEdge >= -GROUND_WIDTH / 2 &&
    playerLeftEdge < GROUND_WIDTH / 2 &&
    playerBottomEdge >= GROUND_LEVEL
      ? playerBottomEdge - GROUND_LEVEL
      : Infinity;
  const occupiedSlotBelow = findOccupiedSlotBelow(
    { x: player.position.x, y: playerBottomEdge },
    state
  );
  if (!occupiedSlotBelow) {
    return distanceToGround;
  }
  const slotPosition = calculateSlotPosition(
    occupiedSlotBelow.column,
    occupiedSlotBelow.row
  );
  const slotTopEdge = slotPosition.y + SLOT_HEIGHT / 2;
  return playerBottomEdge - slotTopEdge;
};

export const isRowFullyOccupied = (row: number, state: State) => {
  for (let column = 0; column < GRID_COLUMNS; column++) {
    if (getWreckInSlot(row, column, state) === null) {
      return false;
    }
  }
  return true;
};

export const findHightestOccupiedRow = (state: State): number => {
  return state.entities.reduce((acc, entity) => {
    if (!isWreck(entity)) {
      return acc;
    }
    if (entity.slot === null) {
      return acc;
    }
    if (entity.slot.row > acc) {
      return entity.slot.row;
    }
    return acc;
  }, -1);
};

export const findHighestFilledRow = (state: State) => {
  const highestOccupiedRow = findHightestOccupiedRow(state);
  for (let row = highestOccupiedRow; row >= 0; row--) {
    if (isRowFullyOccupied(row, state)) {
      return row;
    }
  }
  return -1;
};

export const isWreckIndestructible = (
  wreckId: string,
  state: State
): boolean => {
  const wreck = entityById(wreckId, state.entities);
  if (!wreck) {
    throw new Error(`Wreck with id ${wreckId} not found!`);
  }
  if (wreck.type !== "wreck") {
    throw new Error(`Entity is not a wreck`);
  }
  return wreck.slot !== null && isRowFullyOccupied(wreck.slot.row, state);
};

const GROUND_BOX = {
  top: GROUND_LEVEL,
  bottom: GROUND_LEVEL - 100,
  left: -GROUND_WIDTH / 2,
  right: GROUND_WIDTH / 2,
};

const isAssignedWreck = (entity: Entity): entity is AssignedWreck =>
  entity.type === "wreck" && entity.slot !== null;

const movePlayer = (
  timeDelta: number,
  state: State,
  { position, velocity }: { position: Point; velocity: Point },
  iteration: number = 0
): { position: Point; velocity: Point } => {
  if (iteration > 5) {
    return { position, velocity };
  }
  const intersection: RayIntersection | null = [
    GROUND_BOX,
    ...state.entities
      .filter(isAssignedWreck)
      .filter(
        (entity) =>
          distance(
            entity.position,
            calculateSlotPosition(entity.slot.column, entity.slot.row)
          ) <= PRECISION
      )
      .map((wreck) => slotToBox(wreck.slot.row, wreck.slot.column)),
  ].reduce((acc, box) => {
    const i = rayBoxIntersection(box, {
      origin: {
        x: position.x,
        y: position.y - PLAYER_HEIGHT / 2,
      },
      direction: vectorMul(timeDelta, velocity),
    });
    if (i === null) {
      return acc;
    }
    if (acc === null || i.value < acc.value) {
      return i;
    }
    return acc;
  }, null as RayIntersection | null);

  const OFFSET = 0.1;

  const offset = OFFSET / vectorLength(vectorMul(timeDelta, velocity));

  if (!intersection || intersection.value > 1 + offset) {
    return {
      position: vectorAdd(position, vectorMul(timeDelta, velocity)),
      velocity,
    };
  }

  const moveAmount = Math.max(0, intersection.value - offset);

  const newPosition = vectorAdd(
    position,
    vectorMul(moveAmount * timeDelta, velocity)
  );

  return movePlayer(
    (1 - moveAmount) * timeDelta,
    state,
    {
      position: newPosition,
      velocity:
        intersection.type === "horizontal"
          ? { x: velocity.x, y: 0 }
          : { x: 0, y: velocity.y },
    },
    iteration + 1
  );
};

const tickPhysics = (timeDelta: number, state: State): State => {
  return {
    ...state,
    entities: state.entities.map((entity) => {
      switch (entity.type) {
        case "player": {
          const playerVelocity = {
            x: entity.moveIntent * PLAYER_MOVEMENT_SPEED,
            y: entity.velocityY - timeDelta * GRAVITY,
          };
          const { velocity, position } = movePlayer(timeDelta, state, {
            velocity: playerVelocity,
            position: entity.position,
          });
          return {
            ...entity,
            position,
            velocityY: velocity.y,
          };
        }
        case "projectile": {
          return {
            ...entity,
            position: vectorAdd(
              entity.position,
              vectorMul(timeDelta * PLAYER_PROJECTILE_SPEED, entity.velocity)
            ),
          };
        }
        case "enemy": {
          return {
            ...entity,
            direction: {
              x: entity.direction.x,
              y: entity.direction.y,
            },
            position: vectorAdd(
              entity.position,
              vectorMul(timeDelta * ENEMY_SPEED, entity.direction)
            ),
            lifetime: entity.lifetime + timeDelta,
          };
        }
        case "wreck": {
          if (entity.lifetime < WRECK_FALL_DELAY) {
            return {
              ...entity,
              lifetime: entity.lifetime + timeDelta,
            };
          }
          return {
            ...entity,
            velocityY: entity.velocityY + timeDelta * GRAVITY,
            position: (function () {
              if (!entity.slot) {
                return vectorAdd(
                  entity.position,
                  vectorMul(timeDelta * entity.velocityY, { x: 0, y: -1 })
                );
              }
              const slotPosition = calculateSlotPosition(
                entity.slot.column,
                entity.slot.row
              );
              const distanceToTarget = distance(entity.position, slotPosition);
              if (distanceToTarget === 0) {
                return entity.position;
              }
              const direction = identityVector(
                vectorSub(slotPosition, entity.position)
              );
              return vectorAdd(
                entity.position,
                vectorMul(
                  Math.min(timeDelta * entity.velocityY, distanceToTarget),
                  direction
                )
              );
            })(),
            lifetime: entity.lifetime + timeDelta,
          };
        }
        default: {
          return entity;
        }
      }
    }),
  };
};

type Collision = {
  a: string;
  b: string;
};

// From: http://jeffreythompson.org/collision-detection/circle-rect.php
const isCircleAndRectangleIntersecting = (
  cx: number,
  cy: number,
  radius: number,
  top: number,
  right: number,
  bottom: number,
  left: number
) => {
  // temporary variables to set edges for testing
  let testX = cx;
  let testY = cy;

  // which edge is closest?
  if (cx < left) testX = left; // test left edge
  else if (cx > right) testX = right; // right edge
  if (cy < bottom) testY = bottom; // bottom edge
  else if (cy > top) testY = top; // top edge

  // get distance from closest edges
  const distX = cx - testX;
  const distY = cy - testY;
  const distance = Math.sqrt(distX * distX + distY * distY);

  // if the distance is less than the radius, collision!
  if (distance <= radius) {
    return true;
  }
  return false;
};

const isProjectileCollidingWithPlayer = (
  projectile: Projectile,
  player: Player
): boolean => {
  return isCircleAndRectangleIntersecting(
    projectile.position.x,
    projectile.position.y,
    PROJECTILE_RADIUS,
    player.position.y + PLAYER_HEIGHT / 2,
    player.position.x + PLAYER_WIDTH / 2,
    player.position.y - PLAYER_HEIGHT / 2,
    player.position.x - PLAYER_WIDTH / 2
  );
};

const isHealthPowerUpCollidingWithPlayer = (
  powerUp: HealthPowerUp,
  player: Player
): boolean => {
  return isCircleAndRectangleIntersecting(
    powerUp.position.x,
    powerUp.position.y,
    HEALTH_POWER_UP_RADIUS,
    player.position.y + PLAYER_HEIGHT / 2,
    player.position.x + PLAYER_WIDTH / 2,
    player.position.y - PLAYER_HEIGHT / 2,
    player.position.x - PLAYER_WIDTH / 2
  );
};

const isProjectileCollidingWithEnemy = (
  projectile: Projectile,
  enemy: Enemy
): boolean => {
  return distance(projectile.position, enemy.position) < ENEMY_RADIUS;
};

const isColliding = (a: Entity, b: Entity): boolean => {
  if (a.type === "projectile" && b.type === "player") {
    return isProjectileCollidingWithPlayer(a, b);
  }
  if (a.type === "health-power-up" && b.type === "player") {
    return isHealthPowerUpCollidingWithPlayer(a, b);
  }
  if (a.type === "projectile" && b.type === "enemy") {
    return isProjectileCollidingWithEnemy(a, b);
  }
  return false;
};

export const findCollisions = (state: State): Collision[] => {
  return state.entities.reduce((acc, entity) => {
    acc.push(
      ...state.entities
        .filter(
          (otherEntity) =>
            (otherEntity.type === "player" || otherEntity.type === "enemy") &&
            isColliding(entity, otherEntity)
        )
        .map(
          (otherEntity): Collision => ({
            a: entity.id,
            b: otherEntity.id,
          })
        )
    );
    return acc;
  }, [] as Collision[]);
};

export const entityById = (
  entityId: string,
  entities: Entity[]
): Entity | undefined => {
  return entities.find((entity) => entity.id === entityId);
};

const removeEntityById = (
  entityToRemoveId: string,
  entities: Entity[]
): Entity[] => {
  return entities.filter((entity) => entity.id !== entityToRemoveId);
};

export const isWreck = (entity: Entity): entity is Wreck =>
  entity.type === "wreck";

export const findFreeSlotBelow = (
  position: Point,
  state: State
): { row: number; column: number } | null => {
  const slot = getSlot(position);
  if (!slot) {
    return null;
  }
  const column = slot.column;
  const slotPosition = calculateSlotPosition(slot.column, slot.row);
  const hightestPossibleRow =
    slotPosition.y < position.y ? slot.row : slot.row - 1;
  for (let row = 0; row <= hightestPossibleRow; row++) {
    if (!isSlotReservedOrOccupied(row, column, state)) {
      return { row, column };
    }
  }
  return null;
};

const isPlayer = (entity: Entity): entity is Player => entity.type === "player";

const processEnemyShooting = (timeDelta: number, state: State): State => {
  return {
    ...state,
    entities: state.entities.reduce((newEntities, entity) => {
      if (entity.type !== "enemy") {
        return newEntities;
      }
      const player: Player | undefined = newEntities.find(isPlayer);
      if (
        entity.position.y < 500 &&
        entity.timeSinceLastFired > ENEMY_FIRING_INTERVAL &&
        player
      ) {
        return applyToEntityById(
          () => ({
            ...entity,
            timeSinceLastFired: 0,
          }),
          entity.id,
          [
            ...newEntities,
            {
              id: cuid(),
              type: "projectile",
              position: entity.position,
              velocity: vectorMul(
                ENEMY_PROJECTILE_SPEED,
                identityVector(vectorSub(player.position, entity.position))
              ),
              ownerId: entity.id,
            },
          ]
        );
      }
      return applyToEntityById(
        () => ({
          ...entity,
          timeSinceLastFired: (entity.timeSinceLastFired += timeDelta),
        }),
        entity.id,
        newEntities
      );
    }, state.entities),
  };
};

const cleanUp = (state: State): State => {
  return {
    ...state,
    entities: state.entities.filter(
      (entity) => vectorLength(entity.position) < 1500
    ),
  };
};

const unstuckPlayers = (state: State): State => {
  return {
    ...state,
    entities: state.entities.filter(isPlayer).reduce((newEntities, player) => {
      const slot = getSlot({
        x: player.position.x,
        y: player.position.y - PLAYER_HEIGHT / 2,
      });
      if (!slot || getWreckInSlot(slot.row, slot.column, state) === null) {
        return newEntities;
      }
      return applyToEntityById(
        () => {
          return {
            ...player,
            position: {
              x: player.position.x,
              y: player.position.y + SLOT_HEIGHT,
            },
          };
        },
        player.id,
        newEntities
      );
    }, state.entities),
  };
};

const tickClock = (timeDelta: number, state: State) => {
  return {
    ...state,
    time: state.time + timeDelta,
  };
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "TICK": {
      return tickClock(
        action.timeDelta,
        processEnemyShooting(
          action.timeDelta,
          unstuckPlayers(tickPhysics(action.timeDelta, cleanUp(state)))
        )
      );
    }
    case "MOVE": {
      return {
        ...state,
        entities: applyToEntityById(
          (entity) => ({ ...entity, moveIntent: action.moveIntent }),
          action.playerId,
          state.entities
        ),
      };
    }
    case "JUMP": {
      const player = entityById(action.playerId, state.entities) as
        | Player
        | undefined;
      if (!player || playerDistanceToNearestPlatform(player, state) > 50) {
        return state;
      }
      return {
        ...state,
        entities: applyToEntityById(
          (entity) => ({ ...entity, velocityY: PLAYER_JUMP_SPEED }),
          action.playerId,
          state.entities
        ),
      };
    }
    case "SHOOT": {
      const player = entityById(action.playerId, state.entities) as
        | Player
        | undefined;
      if (!player) {
        return state;
      }
      return {
        ...state,
        entities: [
          ...state.entities,
          {
            id: cuid(),
            type: "projectile",
            position: player.position,
            velocity: vectorMul(PLAYER_PROJECTILE_SPEED, action.direction),
            ownerId: player.id,
          },
        ],
      };
    }
    case "SPAWN_ENEMY": {
      const player = state.entities.find((entity) => entity.type === "player");
      if (!player) {
        return state;
      }
      const direction = Math.random() > 0.5 ? 1 : -1;
      return {
        ...state,
        enemySpawnedAt: state.time,
        entities: [
          ...state.entities,
          {
            id: cuid(),
            type: "enemy",
            subType: "normal",
            position: {
              x: -direction * 900,
              y: 300 + player.position.y + Math.random() * 150 - 75,
            },
            health: ENEMY_INITIAL_HEALTH,
            timeSinceLastFired: ENEMY_FIRING_INTERVAL,
            lifetime: 0,
            direction: { x: direction, y: 0 },
          },
        ],
      };
    }
    case "DO_DAMAGE": {
      return {
        ...state,
        entities: applyToEntityById(
          (entity) => {
            if (entity.type === "player" || entity.type === "enemy") {
              return { ...entity, health: entity.health - action.amount };
            }
            return entity;
          },
          action.targetId,
          state.entities
        ),
      };
    }
    case "REMOVE_PROJECTILE": {
      return {
        ...state,
        entities: removeEntityById(action.projectileId, state.entities),
      };
    }
    case "DIE": {
      return {
        ...state,
        entities: removeEntityById(action.targetId, state.entities),
      };
    }
    case "SPAWN_WRECK": {
      const wreck: Wreck = {
        id: action.id,
        type: "wreck",
        position: action.position,
        lifetime: 0,
        velocityY: 0,
        slot: action.shotPosition
          ? findFreeSlotBelow(action.shotPosition, state)
          : null,
      };
      return {
        ...state,
        entities: [...state.entities, wreck],
      };
    }
    case "REPLENISH_HEALTH": {
      return {
        ...state,
        entities: applyToEntityById(
          (player) => {
            if (player.type !== "player") {
              throw new Error(`Entity ${player.id} is not of type 'player'`);
            }
            return {
              ...player,
              health: Math.min(
                player.health + HEALTH_POWER_UP_AMOUNT,
                PLAYER_INITIAL_HEALTH
              ),
            };
          },
          action.targetId,
          state.entities
        ),
      };
    }
    case "SPAWN_POWER_UP": {
      return {
        ...state,
        lastPowerUpSpawnY: action.position.y,
        entities: [
          ...state.entities,
          {
            id: action.id,
            type: "health-power-up",
            position: action.position,
          },
        ],
      };
    }
    case "INCREASE_SCORE": {
      return {
        ...state,
        score: state.score + action.amount,
      };
    }
    case "RESTART": {
      return INITIAL_STATE;
    }
  }
};

export default reducer;
